import { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import clsx from "clsx";
import { TextField, Paper } from "@mui/material";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { createUseStyles } from "$utils/createUseStyles";
import { useAppDispatch } from "$store";
import { theme } from "$utils/theme/bataillog";
import { Text, Layout, Popin } from "$components";
import useAuth from "$hooks/useAuth";
import { publicIconPath } from "$utils/constants";
import { EditUsers } from "$services/Coordinators.service";
import { userInfo as actionUpdateUserInfo } from "$actions/auth";
import UpdatePasswordForm from "./UpdatePasswordForm";
import { RecentDownloads } from "./RecentDownloads";

function Main() {
  const { user, token, isAdmin } = useAuth();
  const dispatch = useAppDispatch();
  const [email] = useState<string>(user?.userInfo.email || "");
  const [firstname, setFirstname] = useState<string>(
    user?.userInfo.firstname || ""
  );
  const [lastname, setLastname] = useState<string>(
    user?.userInfo.lastname?.toUpperCase() || ""
  );
  const [phone, setPhone] = useState<string>(user?.userInfo.phone || "");
  const [job, setJob] = useState<string>(user?.userInfo.job || "");
  const [password] = useState<string>(user?.password || "********");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const classes = useStyles();
  const firstnameRef = useRef<any>(null);
  const lastnameRef = useRef<any>(null);
  const phoneRef = useRef<any>(null);
  const jobRef = useRef<any>(null);
  const {
    control,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const toggleDrawer = () => {
    setIsOpen(false);
  };
  const handleChangeFirstName = () => (event: any) => {
    setFirstname(event.target.value);
  };
  const handleChangeLastName = () => (event: any) => {
    setLastname(
      event.target.value
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
    );
  };
  const handleChangePhoneNumber = () => (event: any) => {
    const re = /^[0-9\b]+$/;
    if (
      (event.target.value === "" || re.test(event.target.value)) &&
      event.target.value.length <= 10
    ) {
      setPhone(event.target.value);
    }
  };
  const handleChangeJob = () => (event: any) => {
    setJob(event.target.value);
  };
  const handleBlurOnFirstName = async () => {
    if (firstname === "") {
      setError("firstname", {
        type: "required",
        message: "Veuillez remplir ce champ",
      });
      firstnameRef?.current?.focus();
    } else if (user && user.userInfo) {
      setError("firstname", {});
      await EditUsers(
        token,
        {
          user: {
            firstname,
          },
        },
        user.userInfo.id
      );
      dispatch(actionUpdateUserInfo({ ...user.userInfo, firstname }));
    }
  };
  const handleBlurOnLastName = async () => {
    if (lastname === "") {
      setError("lastname", {
        type: "required",
        message: "Veuillez remplir ce champ",
      });
      lastnameRef?.current?.focus();
    } else if (user && user.userInfo) {
      setError("lastname", {});
      await EditUsers(
        token,
        {
          user: {
            lastname: lastname?.toUpperCase(),
          },
        },
        user.userInfo.id
      );
      dispatch(actionUpdateUserInfo({ ...user.userInfo, lastname }));
    }
  };
  const handleBlurOnPhoneNumber = async () => {
    if (phone !== "" && !phone.match(/^[0-9]{10,}$/)) {
      setError("phone", {
        type: "pattern",
        message: "Veuillez saisir un numéro de téléphone valide",
      });
      phoneRef?.current?.focus();
    } else if (user && user.userInfo) {
      setError("phone", {});
      await EditUsers(
        token,
        {
          user: {
            phone,
          },
        },
        user.userInfo.id
      );
      dispatch(actionUpdateUserInfo({ ...user.userInfo, phone }));
    }
  };
  const handleBlurOnJob = async () => {
    if (job === "") {
      setError("job", {
        type: "required",
        message: "Veuillez remplir ce champ",
      });
      jobRef?.current?.focus();
    } else if (user && user.userInfo) {
      setError("job", {});
      await EditUsers(
        token,
        {
          user: {
            job,
          },
        },
        user.userInfo.id
      );
      dispatch(actionUpdateUserInfo({ ...user.userInfo, job }));
    }
  };

  return (
    <Layout title="Mon profil" className={classes.container}>
      <Paper className={`${classes.ProfilePageMainContainer}`}>
        <div className={classes.ProfilePage__Head}>
          <div>
            <img
              alt="user-black-icon"
              src={`${publicIconPath}/user-black.svg`}
            />
            <Text type="h3">Informations utilisateur</Text>
          </div>
        </div>
        <Container className={classes.container}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Controller
                name="lastname"
                control={control}
                rules={{
                  pattern: {
                    value: /^[\\pM\\pLA-Z ,.'-]+$/,
                    message: "Veuillez saisir un nom en majuscules uniquement",
                  },
                }}
                render={() => (
                  <>
                    <TextField
                      className={classes.TextField}
                      fullWidth
                      inputRef={lastnameRef}
                      id="lastname"
                      label="Nom"
                      variant="outlined"
                      value={lastname}
                      onChange={handleChangeLastName()}
                      onBlur={handleBlurOnLastName}
                    />
                    <Text className={classes.errorField}>
                      {errors.lastname &&
                        (errors.lastname.message as unknown as string)}
                    </Text>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="firstname"
                control={control}
                render={() => (
                  <>
                    <TextField
                      className={classes.TextField}
                      fullWidth
                      inputRef={firstnameRef}
                      id="firstname"
                      label="Prénom"
                      variant="outlined"
                      value={firstname}
                      onChange={handleChangeFirstName()}
                      onBlur={handleBlurOnFirstName}
                    />
                    <Text className={classes.errorField}>
                      {errors.firstname &&
                        (errors.firstname.message as unknown as string)}
                    </Text>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="Email"
                control={control}
                render={() => (
                  <TextField
                    className={clsx(
                      classes.TextField,
                      classes.disableTextField
                    )}
                    fullWidth
                    id="email"
                    label="Email"
                    variant="outlined"
                    value={email}
                    disabled
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="Téléphone"
                control={control}
                render={() => (
                  <>
                    <TextField
                      id="phone"
                      fullWidth
                      inputRef={phoneRef}
                      label="Téléphone"
                      variant="outlined"
                      onChange={handleChangePhoneNumber()}
                      onBlur={handleBlurOnPhoneNumber}
                      value={phone}
                    />
                    <Text className={classes.errorField}>
                      {errors.phone &&
                        (errors.phone.message as unknown as string)}
                    </Text>
                  </>
                )}
              />
            </Grid>
            {!isAdmin() && (
              <Grid item xs={12} md={3}>
                <Controller
                  name="job"
                  control={control}
                  render={() => (
                    <>
                      <TextField
                        fullWidth
                        inputRef={jobRef}
                        id="firstname"
                        label="Fonction"
                        variant="outlined"
                        value={job}
                        onChange={handleChangeJob()}
                        onBlur={handleBlurOnJob}
                      />
                      <Text className={classes.errorField}>
                        {errors.job &&
                          (errors.job.message as unknown as string)}
                      </Text>
                    </>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Container>
      </Paper>
      <Paper
        className={clsx(
          classes.ProfilePageMainContainer,
          classes.PasswordSection
        )}
      >
        <div className={classes.ProfilePage__Head}>
          <div>
            <img alt="password-icon" src={`${publicIconPath}/lock.svg`} />
            <Text type="h3">Mot de passe</Text>
          </div>
          <img
            className={classes.PencilImg}
            alt="edit-icon"
            src={`${publicIconPath}/pencil.svg`}
            onClick={() => setIsOpen(true)}
            onKeyDown={() => setIsOpen(false)}
          />
        </div>
        <Container className={classes.container}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Controller
                name="password"
                control={control}
                render={() => (
                  <TextField
                    fullWidth
                    id="password"
                    type="password"
                    label="Mot de passe"
                    variant="outlined"
                    className={classes.TextField}
                    value={password}
                    onChange={handleChangeLastName()}
                    onBlur={handleBlurOnLastName}
                    disabled
                  />
                )}
              />
            </Grid>
          </Grid>
        </Container>
      </Paper>
      <RecentDownloads />
      <Popin openPopin={isOpen} toggleDrawer={toggleDrawer}>
        <UpdatePasswordForm toggleDrawer={toggleDrawer} />
      </Popin>
    </Layout>
  );
}

const useStyles = createUseStyles(() => ({
  container: {
    margin: "25px 0 25px 0",
  },
  form: {
    flex: 1,
    marginBottom: "10px",
  },
  inputContainer: {
    display: "flex",
    justifyContent: "flex-start",
  },
  ProfilePageMainContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    border: "0px",
    margin: "auto",
    "& td.MuiTableCell-body:first-child": {
      fontWeight: "bold",
    },
    "& .MuiTableContainer-root": {
      boxShadow: "unset",
    },
    "& .MuiIconButton-label": {
      transform: "scale(0.6) !important",
    },
  },
  inputFormat: {
    "&.MuiFormControl-root": {
      fontFamily: theme.font.workSans,
      height: 56,
    },
    "& .MuiInputBase-input": {
      fontFamily: ` ${theme.font.workSans} !important`,
    },
  },
  TextField: {
    composes: "$inputFormat",
  },
  errorField: {
    minHeight: 20,
    color: theme.color.red,
    fontSize: 12,
    maxWidth: 550,
  },
  ProfilePage__Head: {
    padding: " 0 16px",
    borderBottom: "1px solid #d8dadc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h3": {
      marginLeft: "10px",
    },
    "& div:first-child": {
      display: "flex",
      alignItems: "center",
    },
    "& div:last-child": {},
  },
  disableTextField: {
    backgroundColor: "#F4F5F7",
  },
  PencilImg: {
    position: "absolute",
    top: 20,
    right: 14,
    cursor: "pointer",
  },
  PasswordSection: {
    marginTop: 25,
  },
}));

export default Main;
